import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@design-system';
import { fundingPolicyTypeDefs } from '@shared';
import { dispatchPolicyUpdate } from '@/components/PolicyInputForm';

const MAX_CUSTOM_NAME_LENGTH = 60;

interface RenamePolicyDialogProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  fundingSourceId: string;
  policy: PolicyFundingSource;
  multipleFundingSources: MultipleFundingSources;
}

export function RenamePolicyDialog({
  open,
  onClose,
  clientId,
  fundingSourceId,
  policy,
  multipleFundingSources,
}: RenamePolicyDialogProps) {
  const dispatch = useDispatch();
  const defaultName = policy.policyType
    ? fundingPolicyTypeDefs[policy.policyType].label
    : 'Insurance';
  const initialValue =
    policy.policyCustomName != null ? String(policy.policyCustomName) : '';
  const [value, setValue] = useState<string>(initialValue);

  useEffect(() => {
    if (open) {
      setValue(initialValue);
    }
  }, [open, initialValue]);

  const handleSave = () => {
    const trimmed = value.trim();
    const nextValue = trimmed.length === 0 ? null : trimmed;
    const currentValue =
      policy.policyCustomName != null ? String(policy.policyCustomName) : null;
    if (nextValue === currentValue) {
      onClose();
      return;
    }
    dispatchPolicyUpdate(
      dispatch,
      clientId,
      fundingSourceId,
      multipleFundingSources,
      'policyCustomName',
      nextValue,
    );
    onClose();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) onClose();
  };

  const isOverLimit = value.length > MAX_CUSTOM_NAME_LENGTH;

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-primary-dark">Rename policy</DialogTitle>
          <DialogDescription className="text-foreground">
            Give this policy a custom name. Leave blank to use the default name.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-2 py-2">
          <Label
            htmlFor="policy-custom-name"
            className="text-primary-dark"
          >
            Custom name
          </Label>
          <Input
            id="policy-custom-name"
            autoFocus
            value={value}
            placeholder={defaultName}
            maxLength={MAX_CUSTOM_NAME_LENGTH + 1}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !isOverLimit) {
                e.preventDefault();
                handleSave();
              }
            }}
          />
          {isOverLimit && (
            <p className="text-sm text-destructive">
              Name must be {MAX_CUSTOM_NAME_LENGTH} characters or fewer.
            </p>
          )}
          <p className="text-xs text-muted-foreground">Default: {defaultName}</p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isOverLimit}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
